/**
 * Component: Content Tabs
 * -----------------------------------------------------------------------------
 * Custom Element representing a tabbed interface with accessible keyboard controls
 * and URL hash tracking.
 *
 * Usage Example:
 *
 * <content-tabs>
 *   <nav class="tab-buttons" role="tablist" aria-orientation="horizontal">
 *     <button class="tab-button is-active" data-js-tab-button="id1">Tab 1</button>
 *     <button class="tab-button" data-js-tab-button="id2">Tab 2</button>
 *   </nav>
 *   <section class="tab-panels">
 *     <article class="tab-panel is-active" data-js-tab-panel="id1">
 *       <!-- Content for Tab 1 -->
 *     </article>
 *     <article class="tab-panel" data-js-tab-panel="id2">
 *       <!-- Content for Tab 2 -->
 *     </article>
 *     // Panel below will open when you click either the first or second button/tab
 *     <article class="tab-panel" data-js-tab-panel="id1,id2">
 *       <!-- Shared content visible in Tab 1 and Tab 2 -->
 *     </article>
 *   </section>
 * </content-tabs>
 *
 * Notes:
 * - Attributes are required.
 * - CSS classes except `is-active` are optional.
 * - Tab panels can be outside the `<content-tabs>` element.
 *
 * @namespace ContentTabs
 */
(() => {
    const classes = {
      active: 'is-active',
    };
  
    if (!customElements.get('content-tabs')) {
      class ContentTabs extends HTMLElement {
        constructor() {
          super();
  
          this._attributes = {
            tabButton: 'data-js-tab-button',
            tabPanel: 'data-js-tab-panel',
          };
  
          this.selectors = {
            tabButton: '[data-js-tab-button]',
            tabPanel: '[data-js-tab-panel]',
            tabList: '[role="tablist"]',
          };
  
          this.activeTab = ''; // Active tab ID for tracking
          this.isMultipleInstances = false; // Flag for multiple instances on a page
          this.isVertical = false; // "horizontal" is default orientation for tabs / keyboard navigation
  
          this.handleBlockSelect = this.handleBlockSelect.bind(this);
          this.handleHashChange = this.handleHashChange.bind(this);
          this.handleTabClick = this.handleTabClick.bind(this);
          this.handleTabKeydown = this.handleTabKeydown.bind(this);
        }
  
        /**
         * Invoked each time the custom element is connected to the DOM.
         */
        connectedCallback() {
          this.setDefaultState();
          this.setHtmlElements();
          this.setActiveTabFromHash();
          this.setEventListeners();
        }
  
        /**
         * Invoked each time the custom element is disconnected from the DOM.
         */
        disconnectedCallback() {
          this.removeEventListeners();
        }
  
        /**
         * Set state variables.
         * - used when Element is connected to the DOM.
         */
        setDefaultState() {
          // Check for multiple instances of <content-tabs> Custom Element
          this.isMultipleInstances =
            [...document.querySelectorAll('content-tabs')].length > 1;
  
          this.isVertical =
            this.querySelector(this.selectors.tabList)?.getAttribute(
              'aria-orientation'
            ) === 'vertical';
        }
  
        /**
         * Sets essential HTML elements within the component.
         */
        setHtmlElements() {
          this.tabButtons = [...this.querySelectorAll(this.selectors.tabButton)];
  
          const localPanels = [...this.querySelectorAll(this.selectors.tabPanel)];
          this.tabPanels = localPanels.length
            ? localPanels
            : [...document.querySelectorAll(this.selectors.tabPanel)];
        }
  
        /**
         * Sets the initial active tab based on the URL hash or defaults to the first tab.
         */
        setActiveTabFromHash() {
          // Also skip if multiple instances
          if (
            !this.tabButtons?.length ||
            !this.tabPanels?.length ||
            this.isMultipleInstances
          ) {
            return;
          }
  
          const attributeValue = this.tabButtons?.length
            ? this.tabButtons[0].getAttribute(this._attributes.tabButton) || ''
            : '';
  
          const hashValue = window.location.hash.replace('#', '');
          const initialTabID = this.isValidTabID(hashValue)
            ? hashValue
            : attributeValue;
  
          this.setActiveTab(initialTabID);
        }
  
        /**
         * Sets the active tab button and panel based on the provided tab ID.
         * Updates `this.activeTab` with the new active tab ID.
         * @param {string} activeTab - The ID of the tab to activate.
         */
        setActiveTab(activeTab) {
          if (!activeTab) {
            return;
          }
  
          this.activeTab = activeTab;
  
          /**
           * Wrapping forEach loops inside requestAnimationFrame batches DOM updates
           * to avoid excessive reflows.
           */
          requestAnimationFrame(() => {
            this.tabButtons?.forEach((button) => this.setActiveButton(button));
            this.tabPanels?.forEach((panel) => this.setActivePanel(panel));
          });
        }
  
        /**
         * Sets the active state for a tab button.
         * @param {HTMLElement} button - The tab button element.
         */
        setActiveButton(button) {
          if (!button) {
            return;
          }
  
          const isActive =
            button.getAttribute(this._attributes.tabButton) === this.activeTab;
  
          button.classList.toggle(classes.active, isActive);
          button.setAttribute('aria-selected', isActive);
          button.setAttribute('tabindex', isActive ? '0' : '-1');
        }
  
        /**
         * Sets the active state for a tab panel.
         * @param {HTMLElement} panel - The tab panel element.
         */
        setActivePanel(panel) {
          if (!panel) {
            return;
          }
  
          const associatedTabIds = panel
            .getAttribute(this._attributes.tabPanel)
            ?.split(',')
            .map((id) => id.trim())
            .filter(Boolean);
  
          const isActive = associatedTabIds.includes(this.activeTab);
  
          panel.classList.toggle(classes.active, isActive);
          panel.setAttribute('aria-hidden', !isActive);
        }
  
        /**
         * Activates a tab when its block has been selected within Shopify Customise.
         *
         * @param {Event} event - Shopify's event triggered by selecting a block within Theme Editor.
         */
        handleBlockSelect(event) {
          const targetButton = event?.target;
  
          if (!targetButton?.hasAttribute(this._attributes.tabButton)) {
            return;
          }
  
          const tabId = targetButton.getAttribute(this._attributes.tabButton);
          this.setActiveTab(tabId);
        }
  
        /**
         * Handles a tab button click, setting the URL hash to match the tab data
         * attribute.
         *
         * @param {Event} event - The click event on the tab button.
         */
        handleTabClick(event) {
          event.preventDefault();
          const clickedButton = event.currentTarget;
          const tabId = clickedButton.getAttribute(this._attributes.tabButton);
  
          // Update hash only if there's a single instance of <content-tabs>
          if (
            !this.isMultipleInstances &&
            window.location.hash.replace('#', '') !== tabId
          ) {
            window.location.hash = tabId;
          } else {
            this.setActiveTab(tabId);
          }
        }
  
        /**
         * Handles keyboard navigation between tabs using arrow keys, `Home` and `End`.
         * - Doesn't activate the tab, only moves keyboard focus.
         * - Activation should happen via `Enter` or `Space` (handled natively on
         * <button> element click).
         *
         * @param {KeyboardEvent} event - The keyboard event triggered on a tab button.
         */
        handleTabKeydown(event) {
          const currentIndex = this.tabButtons.indexOf(event.currentTarget);
  
          if (currentIndex === -1) {
            return;
          }
  
          const prevKey = this.isVertical ? 'ArrowUp' : 'ArrowLeft';
          const nextKey = this.isVertical ? 'ArrowDown' : 'ArrowRight';
  
          let newIndex = null;
          let handled = false;
  
          switch (event.key) {
            case prevKey:
              newIndex =
                currentIndex > 0 ? currentIndex - 1 : this.tabButtons.length - 1;
              handled = true;
              break;
  
            case nextKey:
              newIndex =
                currentIndex < this.tabButtons.length - 1 ? currentIndex + 1 : 0;
              handled = true;
              break;
  
            case 'Home':
              newIndex = 0;
              handled = true;
              break;
  
            case 'End':
              newIndex = this.tabButtons.length - 1;
              handled = true;
              break;
          }
  
          if (newIndex !== null) {
            this.tabButtons[newIndex].focus();
          }
  
          if (handled) {
            event.preventDefault();
            event.stopPropagation();
          }
        }
  
        /**
         * Handles the URL hash change, updating the active tab based on the hash.
         */
        handleHashChange() {
          const tabId = window.location.hash.replace('#', '');
  
          if (this.isMultipleInstances || !this.isValidTabID(tabId)) {
            return;
          }
  
          this.setActiveTab(tabId);
        }
  
        /**
         * Checks if a given tab ID exists among registered tab buttons.
         * @param {string} tabId - The tab ID to validate.
         * @returns {boolean} - True if the tab ID is valid.
         */
        isValidTabID(tabId) {
          return this.tabButtons?.some(
            (btn) => btn.getAttribute(this._attributes.tabButton) === tabId
          );
        }
  
        /**
         * Adds event listeners for hash change and tab button clicks.
         */
        setEventListeners() {
          if (!this.tabButtons?.length || !this.tabPanels?.length) {
            return;
          }
  
          if (!this.isMultipleInstances) {
            window.addEventListener('hashchange', this.handleHashChange);
          }
  
          this.tabButtons.forEach((button) => {
            button.addEventListener('click', this.handleTabClick);
            button.addEventListener('keydown', this.handleTabKeydown);
          });
  
          if (Shopify?.designMode) {
            document.addEventListener(
              'shopify:block:select',
              this.handleBlockSelect
            );
          }
        }
  
        /**
         * Removes event listeners.
         */
        removeEventListeners() {
          if (!this.isMultipleInstances) {
            window.removeEventListener('hashchange', this.handleHashChange);
          }
  
          this.tabButtons.forEach((button) => {
            button.removeEventListener('click', this.handleTabClick);
            button.removeEventListener('keydown', this.handleTabKeydown);
          });
  
          if (Shopify?.designMode) {
            document.removeEventListener(
              'shopify:block:select',
              this.handleBlockSelect
            );
          }
        }
      }
  
      customElements.define('content-tabs', ContentTabs);
    }
  })();
  